/**
 * Localized static copy for PDF reports (section titles, column headers, chrome).
 * User-entered names (categories, debts, funds) stay as stored data.
 */
import type { SupportedLocale } from "@repo/core";
import { createTranslator, type TranslateFn } from "../../i18n/labels";
import { ENGLISH_STRINGS } from "../../i18n/translations";

export type PdfTranslate = TranslateFn;

export function createPdfTranslator(locale: SupportedLocale): PdfTranslate {
  return createTranslator(locale);
}

/** Short month labels via Intl (respects UI locale). */
export function monthShort(month1to12: number, locale: string, year = 2026): string {
  try {
    return new Intl.DateTimeFormat(locale, { month: "short" }).format(
      new Date(year, month1to12 - 1, 1)
    );
  } catch {
    return String(month1to12);
  }
}

/**
 * Every static string that appears in the annual PDF (for i18n assertions).
 * Keys map to translation keys; values are resolved via `t`.
 */
export function collectPdfStaticStrings(t: PdfTranslate): string[] {
  const keys = Object.keys(ENGLISH_STRINGS).filter((k) => k.startsWith("pdf."));
  return keys.map((k) => t(k, { year: 2026, month: "May", n: 1, total: 2, date: "…", person: "A", name: "X", actual: "1", target: "2", debt: "1", interest: "1", net: "1", assets: "1", debts: "1" }));
}

/** English originals of PDF keys — must not appear in non-English reports. */
export function englishPdfStaticStrings(): string[] {
  return Object.entries(ENGLISH_STRINGS)
    .filter(([k]) => k.startsWith("pdf."))
    .map(([, v]) => v)
    // Strip template placeholders for substring checks against resolved copy
    .map((v) => v.replace(/\{[^}]+\}/g, "").trim())
    .filter((v) => v.length >= 4);
}
